import type { HeadlinePinPosition } from '../../types/campaign';
import { CopyAllButton, VariationCard } from './variationsCardPrimitives';

// P2b-3 polish round 2 Fix 1: shared popover BODY for the role grids'
// multi-value content cells (body copy / headlines / descriptions).
// Standalone — meant to render inside whatever positioning shell the
// caller provides. ReadOnlyArrayCell wraps it with the click-to-open
// + fixed-position anchoring; the Build View renders the same cards
// inline via <VariationsListInline>.
//
// Layout per round-2 spec:
//   - Header row: title (left) + "Copy all" button (right).
//   - Each variation: bordered card with "Variation N" label (left)
//     + per-card copy button (right).
//   - Empty state: "No variations authored" centred muted text.
//
// Card / Copy-all primitives extracted in Build View commit 4 to
// variationsCardPrimitives so the inline variant matches behaviour
// exactly without copy-paste drift.
//
// Pinned Headlines spec §2.3: optional `pins` prop runs parallel to
// `variations`. Only the Google headlines popover passes it; other
// content cells (body copy, descriptions) omit it.

type Props = {
  title: string;
  variations: string[] | null | undefined;
  pins?: Array<HeadlinePinPosition> | null;
};

export function VariationsListPopover({ title, variations, pins }: Props) {
  const raw = variations ?? [];
  const filtered: Array<{ value: string; pin: HeadlinePinPosition }> = [];
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i];
    if (typeof v !== 'string' || v.length === 0) continue;
    filtered.push({ value: v, pin: pins ? pins[i] ?? null : null });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
        <span className="text-xs uppercase tracking-wide font-medium text-slate-600">
          {title}
        </span>
        {filtered.length > 0 ? (
          <CopyAllButton variations={filtered.map((e) => e.value)} />
        ) : (
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            0 items
          </span>
        )}
      </div>
      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400 italic">
          No variations authored
        </p>
      ) : (
        <ul className="space-y-3">
          {filtered.map((entry, idx) => (
            <VariationCard
              key={idx}
              index={idx + 1}
              value={entry.value}
              pinPosition={entry.pin}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

